/**
 * Use Cases: Tiering Period CRUD Operations
 * Business logic for tiering period detail, create, update, and delete.
 */

import type {
	TieringPeriodDetail,
	TieringPeriodWritePayload,
} from "../model/TieringSettingsModel";
import type { ITieringSettingsRepository } from "../repository/ITieringSettingsRepository";
import { tieringSettingsContainer, TYPES } from "../../di/TieringSettingsModule";

/**
 * Fetch detailed information for a specific tiering period.
 */
export const FetchTieringPeriodDetailUseCase = async (
	id: string,
): Promise<TieringPeriodDetail> => {
	const repository = tieringSettingsContainer.get<ITieringSettingsRepository>(
		TYPES.ITieringSettingsRepository,
	);

	return repository.fetchDetail(id);
};

/**
 * Create a new tiering period.
 * @returns The ID of the newly created period.
 */
export const CreateTieringPeriodUseCase = async (
	payload: TieringPeriodWritePayload,
): Promise<string> => {
	const repository = tieringSettingsContainer.get<ITieringSettingsRepository>(
		TYPES.ITieringSettingsRepository,
	);

	return repository.create(payload);
};

/**
 * Update an existing tiering period.
 */
export const UpdateTieringPeriodUseCase = async (
	id: string,
	payload: TieringPeriodWritePayload,
): Promise<void> => {
	const repository = tieringSettingsContainer.get<ITieringSettingsRepository>(
		TYPES.ITieringSettingsRepository,
	);

	await repository.update(id, payload);
};

/**
 * Toggle the active status of a tiering period.
 */
export const ToggleTieringPeriodStatusUseCase = async (
	id: string,
	active: boolean,
): Promise<void> => {
	const repository = tieringSettingsContainer.get<ITieringSettingsRepository>(
		TYPES.ITieringSettingsRepository,
	);

	await repository.toggleStatus(id, active);
};

/**
 * Delete a tiering period.
 */
export const DeleteTieringPeriodUseCase = async (id: string): Promise<void> => {
	const repository = tieringSettingsContainer.get<ITieringSettingsRepository>(
		TYPES.ITieringSettingsRepository,
	);

	await repository.delete(id);
};

